/**
 * MODAL REDUCER
 *
 * Goals:
 * - Track which modal is open (by name / id)
 * - Store modal-specific data payload (anything the modal needs)
 * - Store meta / config (size, title, preventClose, etc.)
 * - Track isOpen explicitly for clarity
 * - Provide simple "open", "close", "set data", "set meta", "update meta", "clear"
 */

const modalInit = {
  isOpen: false,
  name: null, // string identifier for the modal (e.g., "CONFIRM_DELETE", "EDIT_LOAN", etc.)
  data: null, // arbitrary payload for the modal
  meta: {
    // sensible defaults; override per modal open/update
    title: null,
    size: "md", // e.g. "sm" | "md" | "lg" | "xl" (or whatever your modal system uses)
    preventClose: false, // if true, ignore close attempts (ESC/backdrop)
    closeOnBackdrop: true,
    closeOnEsc: true,
    // Useful for tracking where it was opened from
    source: null, // e.g. route/component name
    // Useful for forcing remount on open if needed
    key: null,
  },
};

const modalReducer = (state = modalInit, action) => {
  let newState = { ...state };

  switch (action.type) {
    /**
     * Set/replace the entire modal state (escape hatch).
     * Expect payload to match modal shape.
     */
    case "SET_MODAL":
      return action.payload;

    /**
     * Open a modal.
     * Payload:
     *  - name (required)
     *  - data (optional)
     *  - meta (optional)
     */
    case "OPEN_MODAL":
      return {
        ...state,
        isOpen: true,
        name: action.payload?.name ?? state.name,
        data: action.payload?.data ?? null,
        meta: {
          ...state.meta,
          ...(action.payload?.meta || {}),
          // default a key on open if one wasn't provided
          key:
            (action.payload?.meta && action.payload.meta.key) ||
            state.meta.key ||
            `${action.payload?.name || state.name || "MODAL"}_${Date.now()}`,
        },
      };

    /**
     * Close the current modal.
     * Keeps name/data/meta by default (so you can animate out / inspect after close).
     * If you want it cleared, call CLEAR_MODAL after closing or use CLOSE_AND_CLEAR_MODAL below.
     */
    case "CLOSE_MODAL":
      newState.isOpen = false;
      return newState;

    /**
     * Close and clear in one shot (optional convenience).
     */
    case "CLOSE_AND_CLEAR_MODAL":
      return modalInit;

    /**
     * Set modal name (useful if you manage open state elsewhere).
     */
    case "SET_MODAL_NAME":
      newState.name = action.payload;
      return newState;

    /**
     * Set/replace modal data.
     */
    case "SET_MODAL_DATA":
      newState.data = action.payload;
      return newState;

    /**
     * Set/replace modal meta entirely.
     */
    case "SET_MODAL_META":
      newState.meta = action.payload;
      return newState;

    /**
     * Merge/patch modal meta.
     */
    case "UPDATE_MODAL_META":
      newState.meta = {
        ...newState.meta,
        ...action.payload,
      };
      return newState;

    /**
     * Clear modal (back to defaults).
     */
    case "CLEAR_MODAL":
      return modalInit;

    default:
      return state;
  }
};

/**
 * ACTION CREATORS
 */

export const modalSet = (modalState) => ({
  type: "SET_MODAL",
  payload: modalState,
});

/**
 * Open a modal with:
 * - name: string (required)
 * - data: any (optional)
 * - meta: object (optional)
 */
export const modalOpen = (name, data = null, meta = {}) => ({
  type: "OPEN_MODAL",
  payload: { name, data, meta },
});

export const modalClose = () => ({
  type: "CLOSE_MODAL",
});

/**
 * Convenience if your UI doesn't need close animations
 * (or you want a hard reset immediately).
 */
export const modalCloseAndClear = () => ({
  type: "CLOSE_AND_CLEAR_MODAL",
});

export const modalSetName = (modalName) => ({
  type: "SET_MODAL_NAME",
  payload: modalName,
});

export const modalSetData = (modalData) => ({
  type: "SET_MODAL_DATA",
  payload: modalData,
});

export const modalSetMeta = (modalMeta) => ({
  type: "SET_MODAL_META",
  payload: modalMeta,
});

/**
 * should be an object with potentially more than one keys to update.
 * it should merge with existing meta values
 */
export const modalMetaUpdate = (updatedModalMetaValues) => ({
  type: "UPDATE_MODAL_META",
  payload: updatedModalMetaValues,
});

export const modalClear = () => ({
  type: "CLEAR_MODAL",
});

export default modalReducer;
